import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Divider, Paper } from "@mui/material";
import { useMap } from "react-leaflet";
import { useAccessContext } from "@/contexts/AccessContext";
import OptionsStep from "@/features/voyagePlanner/wizard/steps/OptionsStep";
import WizardFooter from "@/features/voyagePlanner/wizard/WizardFooter";
import useSpecificationsDraft from "@/features/voyagePlanner/hooks/useSpecificationsDraft";
import useVesselOptions from "@/features/vessel/hooks/useVesselOptions";
import useSpecVessel from "@/features/voyagePlanner/hooks/useSpecVessel";
import usePanelWidth from "@/hooks/usePanelWidth";
import PanelResizeHandle from "@/components/ui/PanelResizeHandle";
import { PANEL_SCROLLBAR_SX, PANEL_HEADER_DIVIDER_SX } from "@/theme/panelStyles";
import { onMouseEnter, onMouseLeave } from "@/utils/mapUx";
import { togglePanel } from "@/reducers/panelsReducer";
import RnwPanelHeader from "@/features/rnwAnalyser/RnwPanelHeader";
import RnwPanelControls from "@/features/rnwAnalyser/RnwPanelControls";
import RnwGeojsonOverlay from "@/features/rnwAnalyser/RnwGeojsonOverlay";
import useRnwAnalyserState from "@/features/rnwAnalyser/useRnwAnalyserState.jsx";
import {
  PANEL_VIEWPORT_GAP,
} from "@/config/layout";
import { useLayoutBounds } from "@/features/layout/layoutBounds.jsx";
const PANEL_TOAST_GAP = 326;

function RnwPanelShell({
  isOpen,
  dispatch,
  rnwLegs,
  setRnwLegs,
  plannerVesselSettings,
  setPlannerVesselSettings,
  cwViaAreasGeojson,
  voyagePlannerPoints,
  calculatorBasespeed,
  setVoyageActive,
  switchRnwAnalyserActive,
  setSwitchRnwAnalyserActive,
  applyRnwSafety,
  setApplyRnwSafety,
  defaultSpeedProfileData,
  speedProfileData,
  consumptionProfileData,
  selectedProfile,
  markedVesselNames,
  setMarkedVesselNames,
  defaultPlannerVesselSettings,
  draftPlannerVesselSettings,
  setDraftPlannerVesselSettings,
  renderAnalyserOverlay,
}) {
  const map = useMap();
  const { accessContext, setAccessContext } = useAccessContext();
  // Two views in one panel: the diagnostics main view and the in-panel
  // specifications view (formerly the Specifications modal).
  const [view, setView] = useState("main");
  const [specTab, setSpecTab] = useState(0);
  const bounds = useLayoutBounds();
  const panelMaxHeight = bounds.panelHeight;
  const panelRef = React.useRef(null);
  const { panelWidth, handleResizeMouseDown } = usePanelWidth({
    storageKey: "rnwPanelWidth",
    map,
    panelRef,
  });

  // Draft-copy lifecycle shared with the voyage planner's Options step.
  const {
    optionsErrors,
    setOptionsError,
    hasOptionsErrors,
    enterSpecs,
    commitIfValid,
  } = useSpecificationsDraft({
    plannerVesselSettings,
    setPlannerVesselSettings,
    draftPlannerVesselSettings,
    setDraftPlannerVesselSettings,
  });

  // Reopening starts on the main view; the specs draft is re-copied on entry.
  useEffect(() => {
    if (!isOpen) setView("main");
  }, [isOpen]);

  // The analyser request carries the real vessel master data — the settings
  // copy starts empty (no default vessel), so running requires a vessel.
  const specVesselId = Number(
    selectedProfile?.vesselId ?? markedVesselNames?.[0]?.vesselId,
  );
  const { specVesselMeta, mergedSettings: analyserVesselSettings, vesselDataComplete } =
    useSpecVessel({ vesselId: specVesselId, plannerVesselSettings });
  const vesselReady = Number.isFinite(specVesselId) && vesselDataComplete;

  // Vessel picker on the main view — same global selection the voyage
  // planner mirrors (markedVesselNames), so the panels stay in sync.
  const vesselOptions = useVesselOptions();
  const selectedVessel = useMemo(
    () =>
      vesselOptions.find((option) => Number(option?.id) === specVesselId) ??
      null,
    [vesselOptions, specVesselId],
  );
  const handleVesselChange = useCallback(
    (_e, value) => {
      const nextVesselId = Number(value?.id);
      setMarkedVesselNames(
        Number.isFinite(nextVesselId)
          ? [{ vesselId: nextVesselId, vesselName: value?.name ?? "" }]
          : [],
      );
    },
    [setMarkedVesselNames],
  );

  const toastPosition = useMemo(() => {
    return {
      top: bounds.top,
      right: Math.max(PANEL_VIEWPORT_GAP, bounds.viewportWidth - (bounds.left + panelWidth) - PANEL_TOAST_GAP),
    };
  }, [bounds, panelWidth]);

  const {
    fetchRnwLegs,
    geojsonVersion,
    handleCombinedToggle,
    handleLayerRendered,
    handleMetricChange,
    isFetchingAnalyser,
    isRenderingLayer,
    selectedMetrics,
    setShowCachedAnalyserToast,
    showCachedAnalyserToast,
    switchCombinedRnw,
    clearAnalyserData,
    hasAnalyserResult,
    isAnalyserResultStale,
  } = useRnwAnalyserState({
    voyagePlannerPoints,
    calculatorBasespeed,
    plannerVesselSettings: analyserVesselSettings,
    speedProfileData,
    consumptionProfileData,
    defaultSpeedProfileData,
    cwViaAreasGeojson,
    applyRnwSafety,
    rnwLegs,
    setRnwLegs,
    accessContext,
    setAccessContext,
  });

  const rnwUiDisabled = !applyRnwSafety;
  // Busy until the layer is actually on the map, not just until the fetch
  // resolves — drawing the huge geojson takes noticeable time itself.
  const analyserBusy =
    isFetchingAnalyser || (isRenderingLayer && switchRnwAnalyserActive);
  const hasEnoughPlannerPoints =
    Array.isArray(voyagePlannerPoints?.features) &&
    voyagePlannerPoints.features.length >= 2;
  const analyserStatusLabel = analyserBusy
    ? "Running analysis"
    : !vesselReady
      ? "Select a vessel first"
      : isAnalyserResultStale
        ? "Settings changed – run again to update"
        : hasEnoughPlannerPoints
          ? "Ready to analyse"
          : "";

  const handleClose = useCallback(() => {
    dispatch(togglePanel("rnw"));
    onMouseLeave(map);
  }, [dispatch, map]);

  const handleVisibilityChange = useCallback(
    (event) => {
      setSwitchRnwAnalyserActive(event.target.checked);
    },
    [setSwitchRnwAnalyserActive],
  );

  const handleRnwSpecificationsButtonClick = useCallback(
    (event) => {
      event.stopPropagation();
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      enterSpecs();
      setSpecTab(0);
      setView("specs");
    },
    [enterSpecs],
  );

  // Apply = validate + commit (same semantics as the old modal's Apply);
  // Back discards the draft edits — the draft is re-copied on next entry.
  const handleApplySpecifications = () => {
    if (commitIfValid() == null) return;
    setView("main");
  };

  return (
    <>
      {isOpen && (
        <Paper
          key="rnw-panel"
          id="rnw-panel"
          ref={panelRef}
          elevation={0}
          sx={{
            position: "absolute",
            top: bounds.top,
            zIndex: 1000,
            bgcolor: "background.paper",
            width: `${panelWidth}px`,
            color: "#004f7d",
            borderRadius: "0px",
            marginLeft: `${bounds.left}px`,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            height: `${panelMaxHeight}px`,
            boxSizing: "border-box",
            p: 1.5,
          }}
          onMouseEnter={() => {
            setVoyageActive(false);
            onMouseEnter(map);
          }}
          onMouseLeave={() => {
            setVoyageActive(true);
            onMouseLeave(map);
          }}
        >
          <RnwPanelHeader
            switchRnwAnalyserActive={switchRnwAnalyserActive}
            onToggleVisibility={handleVisibilityChange}
            onClose={handleClose}
            subtitle={
              view === "specs"
                ? "Specifications"
                : "Route network diagnostics"
            }
          />
          <Divider sx={PANEL_HEADER_DIVIDER_SX} />
          <Box
            sx={{
              flex: "1 1 auto",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              // The main view scrolls here; the specs view (OptionsStep) owns
              // its own scroll with a fixed tab header, so this stays overflow
              // hidden for it and auto for the main controls.
              overflowY: view === "specs" ? "hidden" : "auto",
              overflowX: "hidden",
              overscrollBehavior: "contain",
              ...PANEL_SCROLLBAR_SX,
            }}
          >
            {view === "main" ? (
              <RnwPanelControls
                analyserStatusLabel={analyserStatusLabel}
                applyRnwSafety={applyRnwSafety}
                vesselReady={vesselReady}
                vesselOptions={vesselOptions}
                selectedVessel={selectedVessel}
                onVesselChange={handleVesselChange}
                fetchRnwLegs={fetchRnwLegs}
                onClearAnalyser={clearAnalyserData}
                hasAnalyserResult={hasAnalyserResult}
                handleCombinedToggle={handleCombinedToggle}
                handleMetricChange={handleMetricChange}
                hasEnoughPlannerPoints={hasEnoughPlannerPoints}
                isFetchingAnalyser={analyserBusy}
                onSpecificationsClick={handleRnwSpecificationsButtonClick}
                rnwUiDisabled={rnwUiDisabled}
                selectedMetrics={selectedMetrics}
                setApplyRnwSafety={setApplyRnwSafety}
                setShowCachedAnalyserToast={setShowCachedAnalyserToast}
                showCachedAnalyserToast={showCachedAnalyserToast}
                switchCombinedRnw={switchCombinedRnw}
                switchRnwAnalyserActive={switchRnwAnalyserActive}
                toastPosition={toastPosition}
              />
            ) : (
              <OptionsStep
                specTab={specTab}
                onSpecTabChange={setSpecTab}
                draftPlannerVesselSettings={draftPlannerVesselSettings}
                setDraftPlannerVesselSettings={setDraftPlannerVesselSettings}
                optionsErrors={optionsErrors}
                setOptionsError={setOptionsError}
                vesselLabel={
                  selectedProfile?.vesselName ??
                  markedVesselNames?.[0]?.vesselName ??
                  ""
                }
                vesselMeta={specVesselMeta}
                vesselId={specVesselId}
                defaultPlannerVesselSettings={defaultPlannerVesselSettings}
                showProfileTabs={false}
              />
            )}
          </Box>
          {view === "specs" && (
            <WizardFooter
              secondaryLabel="Back"
              onSecondary={() => setView("main")}
              primaryLabel="Apply Specifications"
              onPrimary={handleApplySpecifications}
              primaryDisabled={hasOptionsErrors}
              primaryHints={
                hasOptionsErrors ? ["Fix the invalid values first"] : null
              }
            />
          )}
        </Paper>
      )}

      <PanelResizeHandle
        visible={isOpen}
        panelWidth={panelWidth}
        panelMaxHeight={panelMaxHeight}
        onMouseDown={handleResizeMouseDown}
      />

      {renderAnalyserOverlay({
        selectedMetrics,
        rnwUiDisabled,
        geojsonVersion,
        onLayerRendered: handleLayerRendered,
        accessContext,
        setAccessContext,
      })}
    </>
  );
}

export default function RnwPanel(props) {
  const {
    rnwLegs,
    cwLegsToViaAreasGeojson,
    setCwLegsToViaAreasGeojson,
    ...rest
  } = props;

  const renderAnalyserOverlay = useCallback(
    ({
      selectedMetrics,
      rnwUiDisabled,
      geojsonVersion,
      onLayerRendered,
      accessContext,
      setAccessContext,
    }) => (
      <RnwGeojsonOverlay
        rnwLegs={rnwLegs}
        cwLegsToViaAreasGeojson={cwLegsToViaAreasGeojson}
        setCwLegsToViaAreasGeojson={setCwLegsToViaAreasGeojson}
        cwViaAreasGeojson={rest.cwViaAreasGeojson}
        zoomLevel={rest.zoomLevel}
        dragged={rest.dragged}
        resized={rest.resized}
        geojsonVersion={geojsonVersion}
        selectedMetrics={selectedMetrics}
        rnwUiDisabled={rnwUiDisabled}
        plannerVesselSettings={rest.plannerVesselSettings}
        switchRnwAnalyserActive={rest.switchRnwAnalyserActive}
        onLayerRendered={onLayerRendered}
        accessContext={accessContext}
        setAccessContext={setAccessContext}
      />
    ),
    [
      rnwLegs,
      cwLegsToViaAreasGeojson,
      rest.cwViaAreasGeojson,
      rest.dragged,
      rest.plannerVesselSettings,
      rest.resized,
      rest.switchRnwAnalyserActive,
      rest.zoomLevel,
      setCwLegsToViaAreasGeojson,
    ],
  );

  return (
    <RnwPanelShell
      {...rest}
      rnwLegs={rnwLegs}
      renderAnalyserOverlay={renderAnalyserOverlay}
    />
  );
}
